import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import ThemeToggle from '../ThemeToggle/ThemeToggle';
import LogoutButton from '../LogoutButton/LogoutButton';
import { useAuth } from '../../context/AuthContext';
import './Sidebar.css';

interface UserStats {
  total_score: number;
  games_played: number;
  high_score: number;
  average_score: number;
}

interface SidebarProps {
  isActive: boolean;
  userStats?: UserStats;
}

const calculateLevel = (score: number): number => {
  if (score <= 0) return 1;
  return Math.floor(score / 5) + 1;
};

const calculateProgress = (score: number): number => {
  const currentLevel = calculateLevel(score);
  const scoreForCurrentLevel = (currentLevel - 1) * 5;
  const scoreForNextLevel = currentLevel * 5;
  const progress = ((score - scoreForCurrentLevel) / (scoreForNextLevel - scoreForCurrentLevel)) * 100;
  return Math.min(Math.max(progress, 0), 100); // Clamp between 0 and 100
};

const Sidebar: React.FC<SidebarProps> = ({ isActive, userStats }) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const { user } = useAuth();

  // Default values if stats aren't loaded yet
  const totalScore = userStats?.total_score || 0;
  const level = calculateLevel(totalScore);
  const progressPercent = calculateProgress(totalScore);
  const nickname = user?.nickname || 'Player';

  return (
    <aside className={`sidebar ${isActive ? 'active' : ''}`}>
      <div className="logo">
        <h1>Codenerdette</h1>
        <span>Gaming Hub</span>
      </div>

      <nav className="nav-menu">
        <Link to="/" className={currentPath === '/' ? 'active' : ''}>
          <span className="icon">
            <i className="lni lni-dashboard"></i>
          </span>
          Dashboard
        </Link>
        <Link to="/games" className={currentPath === '/games' ? 'active' : ''}>
          <span className="icon">
            <i className="lni lni-game"></i>
          </span>
          Games
        </Link>
        <Link to="/statistics" className={currentPath === '/statistics' ? 'active' : ''}>
          <span className="icon">
            <i className="lni lni-bar-chart"></i>
          </span>
          Statistics
        </Link>
        <Link to="/about" className={currentPath === '/about' ? 'active' : ''}>
          <span className="icon">
            <i className="lni lni-users"></i>
          </span>
          About us
        </Link>
      </nav>

      <div className="profile-card">
        <div className="profile-info">
          <div className="level">
            <span>Lvl.</span> <strong>{level}</strong>
            <div className="level-icon">★</div>
          </div>
          <div className="xp-text">{nickname}</div>
          <div className="xp-bar">
            <div 
              className="xp-progress" 
              style={{ width: `${progressPercent}%` }}
            ></div>
          </div>
          <div className="xp-text text-right">{totalScore.toLocaleString()} <span className="xp-text-small">XP</span></div>
          <div className="badge-section">
            <div className="divider-line"></div>
            <div className="yb-text">Your Badge</div>
            <div className="divider-line"></div>
          </div>
          <div className="badges">
            <img src="/d.png" alt="Badge Red" className="badge" />
            <img src="/c.png" alt="Badge Gold" className="badge" />
            <img src="/b.png" alt="Badge Blue" className="badge" />
          </div>
        </div>
      </div>

      <div className="sidebar-bottom">
        <div className="bottom-buttons">
          <ThemeToggle />
          <LogoutButton />
        </div>
      </div>
    </aside>
  );
};

export default Sidebar; 